/**
 * Error de la API tal y como llega al navegador.
 *
 * La API responde RFC 9457 (Problem Details) en **todos** los errores, así
 * que aquí no hace falta adivinar la forma. Lo que sí hace falta es no
 * enseñarle al usuario ni un código ni un `request_id` suelto: la
 * especificación de UX pide errores en lenguaje humano (§8), y el
 * identificador se guarda para soporte, no se pinta como mensaje.
 */

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  retryable?: boolean;
  requestId?: string;
  request_id?: string;
  errors?: Array<{ path?: string; message?: string; code?: string }>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** Mensaje ya legible para una persona. */
    override readonly message: string,
    readonly code: string,
    readonly retryable: boolean,
    /** Para soporte. No se muestra como texto del error. */
    readonly requestId: string | null,
    /** Errores de campo, cuando el fallo es de validación. */
    readonly fieldErrors: ReadonlyArray<{ path: string; message: string }>,
    readonly path: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static from(status: number, body: unknown, path: string): ApiError {
    const problem = (typeof body === 'object' && body !== null ? body : {}) as ProblemDetails;

    return new ApiError(
      status,
      problem.detail ?? problem.title ?? fallbackMessage(status),
      problem.code ?? `HTTP_${status}`,
      problem.retryable ?? (status >= 500 || status === 429),
      problem.requestId ?? problem.request_id ?? null,
      (problem.errors ?? []).map((e) => ({
        path: e.path ?? '',
        message: e.message ?? '',
      })),
      path,
    );
  }

  /** ¿Es un fallo de credenciales, y no un fallo del sistema? */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * Cuando el servidor no dice nada útil.
 *
 * Pasa con un 502 de un balanceador o con la red caída: no hay cuerpo que
 * leer, y «Error 502» no le dice nada a quien está intentando trabajar.
 */
function fallbackMessage(status: number): string {
  if (status === 0) return 'No hay conexión con el servidor.';
  if (status === 401) return 'La sesión ha caducado.';
  if (status === 403) return 'No tiene permiso para esta acción.';
  if (status === 404) return 'No se ha encontrado lo que buscaba.';
  if (status === 429) return 'Demasiados intentos. Espere un momento.';
  if (status >= 500) return 'El servidor no ha podido completar la operación.';
  return 'La operación no se ha podido completar.';
}
